import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Backdrop,
  Button,
  FormFieldSet,
  LogoLoader,
  Modal,
  showToast,
  SmartTooltip,
  TableAdv,
} from "../../../../components";
import { mapTableRows } from "../../../../utils";
import AddCompanyModal from "../Modals/AddCompanyModal";
import { useDispatch, useSelector } from "react-redux";
import { formatWords2 } from "../../../../utils/stringUtils";
import {
  fetchCompanyLeadsStart,
  removeCompanyFromState,
} from "../../../../redux/slices/bdmjobs/bdmjobsSlice";
import {
  deleteCompany,
  updateLeadCompany,
} from "../../../../redux/slices/bdmjobs/bdmjobsApi";
import { usePagination } from "../../../../hooks";
import { totalCountStart } from "../../../../redux/slices/totalCount/totalCountSlice";
import EditCompanyModal from "../Modals/EditCompanyModal";
import { useLoginUser } from "../../../../hooks/useLoginUser";

const COMPANY_RET =
  "uiid name linkedInUrl addedBy assignedTo allowOthersToSee status simplyHiredUrl glassdoorUrl zipRecruiterUrl";
const COMPANY_EXTRET = "uiid firstName lastName";

const getValidUrl = (url) => {
  if (!url) return "#";
  return url.startsWith("http://") || url.startsWith("https://")
    ? url
    : `https://${url}`;
};

const Configuration = () => {
  const [openCompanyModal, setOpenCompanyModal] = useState(false);
  const [editCompanyModal, setEditCompanyModal] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [deleteLeadCompany, setDeleteLeadCompany] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [visibilityModal, setVisibilityModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const companyLeadsState = useSelector(
    (state) => state?.bdmJobs?.companyLeads,
  );
  const { value } = useSelector((state) => state.totalCount);
  const { userData: loginUserData } = useLoginUser();

  const companyLeads = companyLeadsState?.data || [];
  const loading = companyLeadsState?.loading || false;
  const totalCount = value?.leadCompanyCount?.data || 0;
  const isAdmin = user?.data?.role?.type === 1;
  const loginUserUiid = loginUserData?.uiid;

  const { page, limit, setPage, handleLimitChange } = usePagination(
    10,
    () => {},
  );

  const refreshList = useCallback(() => {
    dispatch(
      fetchCompanyLeadsStart({
        coll: "LeadCompany",
        lim: limit,
        pg: page,
        ret: COMPANY_RET,
        extret: COMPANY_EXTRET,
        sort: { uiid: -1 },
        ...(!isAdmin && loginUserUiid
          ? { fil: { assignedTo: { type: "NONE", value: loginUserUiid } } }
          : {}),
      }),
    );
  }, [dispatch, limit, page, isAdmin, loginUserUiid]);

  const refreshAll = useCallback(() => {
    dispatch(
      totalCountStart({
        collection: "leadcompanys",
        coll: "LeadCompany",
        tname: "leadCompanyCount",
        app: "ats",
        filters:
          !isAdmin && loginUserUiid
            ? { assignedTo: { type: "NONE", value: loginUserUiid } }
            : {},
      }),
    );
    dispatch(
      fetchCompanyLeadsStart({
        coll: "LeadCompany",
        lim: limit,
        pg: page,
        ret: COMPANY_RET,
        extret: COMPANY_EXTRET,
        sort: { uiid: -1 },
        ...(!isAdmin && loginUserUiid
          ? { fil: { assignedTo: { type: "NONE", value: loginUserUiid } } }
          : {}),
      }),
    );
  }, [dispatch, limit, page, isAdmin, loginUserUiid]);

  useEffect(() => {
    if (!isAdmin && !loginUserUiid) return;
    refreshAll();
  }, [page, limit, isAdmin, loginUserUiid]);

  const updateCompany = async (
    uiid,
    valueObj,
    successMsg = "Updated successfully",
  ) => {
    if (!uiid) return;
    try {
      const res = await updateLeadCompany({
        coll: "LeadCompany",
        uiid,
        payload: [{ type: "SET", value: valueObj }],
      });
      if (res?.data) {
        showToast(successMsg, "success");
        refreshList();
      } else {
        showToast("Failed to update", "error");
      }
    } catch (error) {
      showToast(error?.response?.data?.message || "Failed to update", "error");
    }
  };

  // const handleToggleCompany = (row, checked) => {
  //   return updateCompany(
  //     row.id,
  //     { isEnabled: checked },
  //     `Company ${checked ? "enabled" : "disabled"} successfully`,
  //   );
  // };

  const handleStatusChange = (row) =>
    updateCompany(
      row.id,
      { status: row.status === 1 ? 2 : 1 },
      `Company ${row.status === 1 ? "deactivated" : "activated"} successfully`,
    );

  const handleAssignedToChange = (row, option) =>
    updateCompany(row.id, { assignedTo: option?.value || null });

  const handleDeleteCompany = async () => {
    if (!deleteLeadCompany?.id) {
      showToast("ID not found!", "error");
      return;
    }
    try {
      setDeleteLoading(true);
      const res = await deleteCompany({
        coll: "LeadCompany",
        uiid: deleteLeadCompany.id,
      });
      if (res?.data) {
        showToast("Company deleted successfully", "success");
        dispatch(removeCompanyFromState(deleteLeadCompany.id));
        refreshAll();
      } else {
        showToast("Failed to delete company", "error");
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message || "Failed to delete company",
        "error",
      );
    } finally {
      setShowDeleteAlert(false);
      setDeleteLeadCompany(null);
      setDeleteLoading(false);
    }
  };

  const rows = companyLeads.map((item) => ({
    id: item?.uiid || null,
    companyName: formatWords2(item?.name || ""),
    url: item?.linkedInUrl || "-",
    simplyHiredUrl: item?.simplyHiredUrl || "-",
    zipRecruiterUrl: item?.zipRecruiterUrl || "-",
    glassdoorUrl: item?.glassdoorUrl || "-",
    // isEnabled: item?.isEnabled,
    addedBy: formatWords2(item?.addedBy?.firstName || "-"),
    addedByUiid: item?.addedBy?.uiid || null,
    allowOthersToSee: item?.allowOthersToSee,
    status: item?.status || 1,
    assignedTo: item?.assignedTo
      ? {
          label:
            `${item.assignedTo.firstName || ""} ${item.assignedTo.lastName || ""}`.trim(),
          value: item?.assignedTo?.uiid,
        }
      : "",
  }));

  const columns = [
    {
      key: "companyName",
      label: "Company Name",
      width: "min-w-[250px]! max-w-[300px]!",
      render: (row) => (
        <SmartTooltip
          description={row?.companyName}
          size="xs"
          color="black"
          noicon
          tooltipPosition="bottom"
          portal
        >
          <div className="line-clamp-1">{row?.companyName}</div>
        </SmartTooltip>
      ),
    },
    {
      key: "url",
      label: "LinkedIn URL",
      render: (row) => (
        <a
          href={getValidUrl(row?.url)}
          target="_blank"
          rel="noreferrer"
          className="text-info underline"
        >
          Click
        </a>
      ),
    },
    // {
    //   key: "isEnabled",
    //   label: "Is Enabled",
    //   render: (row) => (
    //     <FormFieldSet
    //       isChecked={row?.isEnabled}
    //       type="toggle"
    //       onChange={(checked) => handleToggleCompany(row, checked)}
    //       noTitle
    //       size="sm"
    //     />
    //   ),
    // },
    { key: "addedBy", label: "Added By" },
    {
      key: "assignedTo",
      label: "Assigned To",
      width: "w-[300px]",
      render: (row) => {
        return loginUserData?.role?.type === 1 ? (
          <FormFieldSet
            type="onlyBDMUsers"
            noTitle
            placeholder="Select"
            size="sm"
            value={row?.assignedTo || null}
            showSearch
            onChange={(option) => handleAssignedToChange(row, option)}
          />
        ) : (
          `${row?.assignedTo?.label}`
        );
      },
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <SmartTooltip
          description={row.status === 1 ? "Deactivate" : "Activate"}
          noicon
        >
          <FormFieldSet
            type="toggle"
            size="sm"
            noTitle
            isChecked={row.status === 1}
            onChange={() => handleStatusChange(row)}
          />
        </SmartTooltip>
      ),
    },
    {
      key: "",
      label: "Action",
      render: (row) => (
        <div className="flex-1 flex items-center gap-2">
          <SmartTooltip description="Edit" noicon>
            <Button
              type="icon"
              variant="text"
              icon="edit_square"
              size="sm"
              color="black"
              onClick={() => {
                setSelectedCompany(row);
                setEditCompanyModal(true);
              }}
            />
          </SmartTooltip>

          <SmartTooltip description="Visibility Settings" noicon>
            <Button
              type="icon"
              variant="text"
              icon="settings"
              size="sm"
              color="black"
              onClick={() => {
                setSelectedCompany(row);
                setVisibilityModal(true);
              }}
            />
          </SmartTooltip>
        </div>
      ),
    },
  ];

  const mappedRows = mapTableRows(rows, columns);

  const handleVisibilityChange = async (checked) => {
    try {
      await updateLeadCompany({
        coll: "LeadCompany",
        uiid: selectedCompany.id,
        payload: [{ type: "SET", value: { allowOthersToSee: checked } }],
      });
      showToast(
        `Company is now ${checked ? "visible" : "restricted"}`,
        "success",
      );
      setVisibilityModal(false);
      setSelectedCompany(null);
      refreshList();
    } catch {
      showToast("Failed to update visibility", "error");
    }
  };

  return (
    <div className="flex-1 flex overflow-auto">
      <TableAdv
        tableId={`ats_priorityleads_configuration_table`}
        columns={columns}
        rows={mappedRows}
        serverPagination
        totalCount={totalCount}
        onSelectionChange={setSelectedRows}
        defaultSelectedRows={selectedRows}
        page={page}
        limit={limit}
        onPaginationChange={setPage}
        onLimitChange={handleLimitChange}
        info="info"
        tdClassName="!py-3"
        toolbarRight={{
          search: true,
          filter: true,
          fullScreen: true,
          add: [
            {
              component: (
                <Button
                  title="Add Company"
                  size="md"
                  icon="add"
                  onClick={() => setOpenCompanyModal(true)}
                />
              ),
            },
          ],
        }}
      />

      <Modal
        isOpen={openCompanyModal}
        onClose={() => setOpenCompanyModal(false)}
        title="Add Company"
        titleColor="primary"
        width="max-w-5xl"
      >
        <AddCompanyModal
          onCancel={() => setOpenCompanyModal(false)}
          onSuccess={() => {
            setOpenCompanyModal(false);
            refreshAll();
          }}
        />
      </Modal>

      <Modal
        isOpen={editCompanyModal}
        onClose={() => {
          setEditCompanyModal(false);
          setSelectedCompany(null);
        }}
        title="Edit Company"
        titleColor="primary"
        width="max-w-5xl"
      >
        <EditCompanyModal
          companyData={selectedCompany}
          onCancel={() => setEditCompanyModal(false)}
          onSuccess={() => {
            setEditCompanyModal(false);
            setSelectedCompany(null);
            refreshAll();
          }}
        />
      </Modal>

      <Modal
        showCloseButton={false}
        isOpen={showDeleteAlert}
        onClose={() => {
          setShowDeleteAlert(false);
          setDeleteLeadCompany(null);
        }}
      >
        <div className="text-center my-3">
          <p>Are you sure you want to delete</p>
          <p className="text-info">"{deleteLeadCompany?.companyName || "-"}"</p>
          <p>from my Companies list?</p>
          <div className="flex justify-center items-center gap-3 mt-8">
            <Button
              title="Cancel"
              variant="outlined"
              color="black"
              onClick={() => {
                setShowDeleteAlert(false);
                setDeleteLeadCompany(null);
              }}
            />
            <Button title="Delete" onClick={handleDeleteCompany} />
          </div>
        </div>
        <Backdrop isOpen={deleteLoading}>
          <LogoLoader />
        </Backdrop>
      </Modal>

      <Modal
        isOpen={visibilityModal}
        onClose={() => {
          setVisibilityModal(false);
          setSelectedCompany(null);
        }}
        title="Company Visibility"
        titleColor="primary"
        width="max-w-md"
      >
        <div className="p-6 space-y-6">
          <p className="text-sm text-gray-600">
            Allow other users to see this company
          </p>
          <FormFieldSet
            type="toggle"
            title="Visible to others"
            isChecked={selectedCompany?.allowOthersToSee}
            onChange={handleVisibilityChange}
          />
        </div>
      </Modal>

      <Backdrop isOpen={loading || deleteLoading}>
        <LogoLoader />
      </Backdrop>
    </div>
  );
};

export default Configuration;
